import { useMemo } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { getTrans, getTransWithParams } from 'modules/i18n';
import { textFor } from '../localizedText';
import {
  signalCountPill,
  signalGridCell,
  signalGridHeader,
  signalGridRowLabel,
  signalMono,
  signalNote
} from '../signalStyles';
import { BlockHeading } from './BlockHeading';
import {
  cellKey,
  readShiftFitAnswer,
  readShiftFitConfig,
  selectedMinutes,
  ShiftFitSlot,
  toggleCell
} from './shiftFit';
import { BlockRendererProps, questionIdOf } from './types';

/**
 * `shift_fit@1`: when someone is free, as a grid of days against time slots.
 *
 * A TABLE, not a pile of buttons. The two axes are real — a cell means "this slot, on this day" — and a
 * table says that structurally, so a screen reader announces "Monday, early shift" from the row and
 * column headers instead of reading forty unlabelled toggles. Every cell is a `button` with
 * `aria-pressed`, which is what makes each one independently answerable rather than part of a widget
 * someone has to learn.
 *
 * The grid scrolls horizontally when there are more columns than fit. A week is seven columns and a
 * phone is not seven columns wide; shrinking cells to fit would take them under the tap target the rest
 * of the flow holds to, and wrapping the days would break the one thing the layout is for — reading
 * down a column to see a whole day.
 *
 * NO DEMAND OVERLAY. pro-core can carry the shift pattern a posting advertises and print it in each
 * cell; this app does not draw it. A cell here carries one meaning, and a bare number beside a tappable
 * square reads as anything from hours to a rank to how many others already took it.
 *
 * The hours total appears only when the operator asked for it, because it is a value pro-core then
 * computes and stores — what a respondent sees and what gets filtered on are the same number.
 */
export const ShiftFitBlock = ({
  block,
  answer,
  onChange,
  locale
}: BlockRendererProps) => {
  const config = useMemo(() => readShiftFitConfig(block), [block]);
  const selected = readShiftFitAnswer(answer)?.selectedCells ?? [];

  const questionId = questionIdOf(block);

  const minutes = selectedMinutes(selected, config);
  const rule = config.durationRule;
  const overLimit = rule !== null && minutes > rule.minutes;

  const { minSelectedCells, maxSelectedCells } = config;
  const atLimit =
    maxSelectedCells !== null && selected.length >= maxSelectedCells;

  const toggle = (key: string) => {
    // The maximum is enforced on the way IN, exactly as the server would, rather than by disabling
    // every unchosen cell: a grid that goes half-inert reads as broken, while a tap that does not
    // add is at least consistent with the line under the counter saying why. Removing always works.
    if (atLimit && !selected.includes(key)) {
      return;
    }
    onChange({ selectedCells: toggleCell(selected, key) });
  };

  /** What the hard cell limits ask for, said once, under the count they bound. */
  const limitTextOf = (): string => {
    if (minSelectedCells !== null && maxSelectedCells !== null) {
      return getTransWithParams('guestForm.shiftFit.between', {
        min: minSelectedCells,
        max: maxSelectedCells
      });
    }
    if (minSelectedCells !== null) {
      return getTransWithParams('guestForm.shiftFit.atLeast', {
        min: minSelectedCells
      });
    }
    if (maxSelectedCells !== null) {
      return getTransWithParams('guestForm.shiftFit.atMost', {
        max: maxSelectedCells
      });
    }
    return '';
  };

  // Resolved once. It was being built twice in one expression — a translation and its parameters
  // rebuilt to ask whether there was anything to say, then again to say it.
  const limitText = limitTextOf();

  const slotTimes = (slot: ShiftFitSlot): string | null =>
    config.showSlotTimes && slot.startTime && slot.endTime
      ? `${short(slot.startTime)}–${short(slot.endTime)}`
      : null;

  return (
    <Stack spacing={3}>
      {/*
        A grid of tappable cells is the one control here that people genuinely misread as "pick one",
        so the hint says otherwise — ALONGSIDE the operator's help rather than instead of it. The
        cell limits are not repeated up here: they belong under the counter, beside the number.
      */}
      <BlockHeading
        block={block}
        locale={locale}
        hint={getTrans('guestForm.shiftFit.hint')}
      />

      {/* `overflowX` on the wrapper rather than the table, so the header row scrolls with its body. */}
      <Box sx={{ overflowX: 'auto', mx: -0.5, px: 0.5 }}>
        <Box
          component="table"
          aria-labelledby={questionId}
          sx={{
            width: '100%',
            borderCollapse: 'separate',
            // 6px gutters instead of borders: the cells already carry their own hairline, and a
            // collapsed table would double every internal edge.
            borderSpacing: '6px',
            // Cells stay tappable rather than compressing to fit; the wrapper scrolls instead.
            minWidth: config.columns.length > 3 ? 380 : undefined
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {/* The corner is empty and must stay silent: it labels nothing. */}
              <Box component="th" scope="col" sx={{ width: 88 }} />
              {config.columns.map(column => (
                <Box
                  component="th"
                  scope="col"
                  key={column.key}
                  sx={signalGridHeader}
                >
                  {textFor(column.label, locale)}
                </Box>
              ))}
            </Box>
          </Box>

          <Box component="tbody">
            {config.slots.map(slot => {
              const times = slotTimes(slot);
              return (
                <Box component="tr" key={slot.key}>
                  <Box
                    component="th"
                    scope="row"
                    sx={{ textAlign: 'left', fontWeight: 400 }}
                  >
                    <Typography sx={signalGridRowLabel}>
                      {textFor(slot.label, locale)}
                    </Typography>
                    {times && (
                      <Typography
                        sx={{
                          ...signalMono,
                          fontSize: 11.5,
                          color: 'text.disabled'
                        }}
                      >
                        {times}
                      </Typography>
                    )}
                  </Box>

                  {config.columns.map(column => {
                    const key = cellKey(column.key, slot.key);
                    const isSelected = selected.includes(key);
                    return (
                      <Box component="td" key={column.key}>
                        <ButtonBase
                          onClick={() => toggle(key)}
                          aria-pressed={isSelected}
                          /*
                            The full label on every cell. The row and column headers give a screen
                            reader the coordinates when it navigates the table, but a respondent
                            tabbing straight through the buttons never hears them — and "pressed"
                            with no subject is not an answer to "what did I just choose".
                          */
                          /*
                            Falling back to the KEY when a label is missing. A label is
                            required and pro-core validates it, so an unlabelled column means
                            the definition is malformed — but the failure mode matters: two
                            empty strings compose to `" "`, which is an accessible name of
                            nothing, and the cell goes silent for exactly the respondent this
                            label exists to serve. `mon early` is a poor name and an infinitely
                            better one than none.
                          */
                          aria-label={`${
                            textFor(column.label, locale) || column.key
                          } ${textFor(slot.label, locale) || slot.key}`}
                          data-testid={`form-cell-${key}`}
                          sx={signalGridCell(isSelected)}
                        >
                          {/* The tick is the second signal, so selection survives greyscale — the
                              border colour is never carrying it alone. */}
                          {isSelected && (
                            <CheckIcon
                              sx={{ fontSize: 16 }}
                              aria-hidden="true"
                            />
                          )}
                        </ButtonBase>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Announced as it changes: a counter nobody hears is no feedback at all for a screen reader,
          and it is the only acknowledgement a tap gets. */}
      <Stack spacing={1} role="status" aria-live="polite">
        {/* Never the positive variant: `shift_fit` has no recommendation, only hard limits, and the
            vocabulary reserves a green counter for advice that has been met. A limit is not an
            achievement — meeting one is simply the answer being allowed. */}
        <Box
          component="span"
          sx={signalCountPill(false)}
          data-testid="form-cell-count"
        >
          {getTransWithParams('guestForm.shiftFit.selected', {
            count: selected.length
          })}
        </Box>

        {/* The limits, which the server enforces and Next is gated on. Without this line a
            respondent meets a dead Next button with nothing on screen explaining it. */}
        {limitText && (
          <Typography variant="caption" color="text.secondary">
            {limitText}
          </Typography>
        )}

        {/* Said only once it is true, because until then it is a warning about nothing. */}
        {atLimit && (
          <Typography variant="caption" color="text.disabled">
            {getTrans('guestForm.shiftFit.limitReached')}
          </Typography>
        )}

        {config.durationSummary && (
          <Typography variant="caption" color="text.secondary">
            {getTransWithParams('guestForm.shiftFit.hours', {
              hours: formatHours(minutes)
            })}
          </Typography>
        )}

        {overLimit && rule && (
          <Box
            sx={signalNote(rule.effect === 'BLOCKING' ? 'caution' : 'neutral')}
          >
            {textFor(rule.message, locale) ||
              getTransWithParams('guestForm.shiftFit.overLimit', {
                hours: formatHours(rule.minutes)
              })}
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

/** `06:00:00` → `06:00`. pro-core accepts seconds, and nobody needs to read them. */
const short = (time: string): string => time.slice(0, 5);

/**
 * Minutes as hours, for reading rather than for arithmetic.
 *
 * One decimal only when there is one, so a whole number of hours does not read as "8.0" — and the
 * comma-vs-point choice is left to `toLocaleString`, because 7.5 hours is "7,5" to a German reader.
 */
const formatHours = (minutes: number): string =>
  (minutes / 60).toLocaleString(undefined, { maximumFractionDigits: 1 });
